import React from 'react';
import Button from '../../components/ui/Button';
import type { DuplicateInvoice } from '../../lib/vendorInvoiceApi';

/**
 * The invoice on screen beside every invoice it matched (VI-5, Q5).
 *
 * The rule is unchanged: same supplier plus the same normalised invoice number.
 * What a reviewer lacked was the evidence to decide with. Deborah was told an
 * invoice "already exists" and could not see that the other one carried a
 * different date, so she could not tell a re-scan from a reused number. The
 * compared cells that differ from this invoice are marked; the file name and
 * upload date always differ and are not.
 *
 * The page passes its own formatters so this adds no second money or date
 * helper.
 */
export interface DuplicateInvoiceComparisonProps {
  current: DuplicateInvoice;
  twins: DuplicateInvoice[];
  formatDate: (value?: string) => string;
  formatMoney: (value: unknown, currency?: string | null) => string;
  onOpenDocument: (invoiceId: string) => void;
}

type Row = { label: string; compare: boolean; value: (inv: DuplicateInvoice) => string };

export function DuplicateInvoiceComparison({
  current,
  twins,
  formatDate,
  formatMoney,
  onOpenDocument,
}: DuplicateInvoiceComparisonProps) {
  const rows: Row[] = [
    { label: 'Invoice number', compare: true, value: (i) => i.invoiceNumber || '-' },
    { label: 'Supplier', compare: true, value: (i) => i.vendorName || '-' },
    { label: 'Invoice date', compare: true, value: (i) => i.invoiceDate || '-' },
    { label: 'Due date', compare: true, value: (i) => i.dueDate || '-' },
    { label: 'Subtotal', compare: true, value: (i) => formatMoney(i.subTotal, i.currency) },
    { label: 'Tax', compare: true, value: (i) => formatMoney(i.taxTotal, i.currency) },
    { label: 'Grand total', compare: true, value: (i) => formatMoney(i.grandTotal, i.currency) },
    { label: 'Lines', compare: true, value: (i) => (i.lineCount == null ? '-' : String(i.lineCount)) },
    {
      label: 'In Inventory',
      compare: false,
      value: (i) => (i.inventoryConfirmedAt ? `Yes, since ${formatDate(i.inventoryConfirmedAt)}` : 'No'),
    },
    { label: 'Uploaded', compare: false, value: (i) => formatDate(i.uploadedAt ?? undefined) },
    { label: 'File', compare: false, value: (i) => i.fileName || '-' },
  ];

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
      <table className="w-full text-xs text-slate-700">
        <thead>
          <tr className="bg-slate-50 text-left">
            <th scope="col" className="px-3 py-1.5 font-semibold text-slate-600" />
            <th scope="col" className="px-3 py-1.5 font-semibold">This invoice</th>
            {twins.map((t, n) => (
              <th key={t._id} scope="col" className="px-3 py-1.5 font-semibold">
                Matched invoice{twins.length > 1 ? ` ${n + 1}` : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const mine = row.value(current);
            return (
              <tr key={row.label} className="border-t border-slate-100">
                <th scope="row" className="whitespace-nowrap px-3 py-1.5 text-left font-medium text-slate-500">
                  {row.label}
                </th>
                <td className="px-3 py-1.5">{mine}</td>
                {twins.map((t) => {
                  const theirs = row.value(t);
                  const differs = row.compare && theirs !== mine;
                  return (
                    <td
                      key={t._id}
                      data-differs={differs ? 'true' : undefined}
                      title={differs ? 'Differs from this invoice' : undefined}
                      className={`px-3 py-1.5 ${differs ? 'bg-rose-50 font-semibold text-rose-800' : ''}`}
                    >
                      {theirs}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr className="border-t border-slate-100">
            <th scope="row" className="px-3 py-1.5 text-left font-medium text-slate-500">
              Document
            </th>
            {[current, ...twins].map((inv) => (
              <td key={inv._id} className="px-3 py-1.5">
                <Button size="sm" onClick={() => onOpenDocument(inv._id)}>
                  Open document
                </Button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default DuplicateInvoiceComparison;
